import { MapPin } from 'lucide-react'
import { useCallback } from 'react'
import { useShallow } from 'zustand/react/shallow'

import useActiveFilterStore from 'src/shared/features/users/stores/marketplace/active-filter'
import { useToast } from 'src/shared/hooks/use-toast'

import { Nearby } from '../../../assets/nearby'

type LocationListItemProps = {
  description: string
  placeId: string
  onSelectItem: (placeId: string, description: string) => void
}

const SUGGESTED_PLACE_IDS = [
  {
    description: 'New York, NY, USA',
    placeId: 'ChIJOwg_06VPwokRYv534QaPC8g',
  },
  {
    description: 'Los Angeles, CA, USA',
    placeId: 'ChIJE9on3F3HwoAR9AhGJW_fL-I',
  },
  {
    description: 'Miami, FL, USA',
    placeId: 'ChIJEcHIDqKw2YgRZU-t3XHylv8',
  },
  {
    description: 'Dallas, TX, USA',
    placeId: 'ChIJS5dFe_cZTIYRj2dH9qSb7Lk',
  },
  {
    description: 'Phoenix, AZ, USA',
    placeId: 'ChIJy3mhUO0SK4cRrBtKNfjHaYw',
  },
]

const SearchNearbyLocationListItem = ({
  onSelectItem,
}: {
  onSelectItem: (placeId: string, description: string) => void
}) => {
  return (
    <li
      className="group/nearby-location-list flex w-full cursor-pointer items-center justify-between rounded-lg"
      onClick={() => {
        onSelectItem('nearby', 'Professionals near me')
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex size-[34px] min-w-[34px] items-center justify-center rounded-sm bg-backgroundOffwhite md:size-[40px] md:min-w-[40px]">
          <Nearby className="size-5 md:size-7" />
        </div>
        <span className="text-[15px] font-semibold md:text-[16px]">Professionals near me</span>
      </div>
      <div className="opacity-0 transition-opacity group-hover/nearby-location-list:opacity-100">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-5"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </div>
    </li>
  )
}

const LocationListItem = ({ description, placeId, onSelectItem }: LocationListItemProps) => {
  return (
    <li
      className="group/location-list flex w-full cursor-pointer items-center justify-between rounded-lg"
      onClick={() => {
        onSelectItem(placeId, description)
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex size-[34px] min-w-[34px] items-center justify-center rounded-sm bg-backgroundOffwhite md:size-[40px] md:min-w-[40px]">
          <MapPin className="size-5 md:size-7" strokeWidth={1} />
        </div>
        <span className="text-[15px] font-semibold md:text-[16px]">{description}</span>
      </div>
      <div className="opacity-0 transition-opacity group-hover/location-list:opacity-100">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-5"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </div>
    </li>
  )
}

type LocationListProps = {
  searchInput: string
  placePredictions: google.maps.places.AutocompletePrediction[]
  onSelectItem: (placeId: string, description: string, isDefault: boolean) => void
  onSelectNearby: () => void
}

const LocationList = ({ searchInput, placePredictions, onSelectItem, onSelectNearby }: LocationListProps) => {
  const { updateSubmittedFilters, setIsUsingExactLocation } = useActiveFilterStore(
    useShallow((state) => ({
      updateSubmittedFilters: state.updateSubmittedFilters,
      setIsUsingExactLocation: state.setIsUsingExactLocation,
    })),
  )

  const { toast } = useToast()

  const fetchUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          updateSubmittedFilters({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          setIsUsingExactLocation(true)
        },
        (error) => {
          console.error('Error getting location:', error)
          toast({
            title: 'Error getting current location',
            description: 'Please allow location access to continue',
          })
          setIsUsingExactLocation(false)
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      )
    } else {
      console.error('Geolocation is not supported by this browser')
      toast({
        title: 'Error getting current location',
        description: 'Geolocation is not supported by this browser',
      })
    }
  }, [updateSubmittedFilters, setIsUsingExactLocation, toast])

  return (
    <div className="flex flex-col gap-2 overflow-y-auto">
      {searchInput.trim() === '' ? (
        <div id="location-predictions" role="listbox" className="w-full">
          {/* <p className="mb-3 text-[13px] font-semibold">Suggestions</p> */}
          <ul
            className="flex max-h-[280px] flex-col gap-[10px] overflow-y-auto
            [&::-webkit-scrollbar-thumb]:rounded-full
            [&::-webkit-scrollbar-thumb]:bg-[#606F6A]
          [&::-webkit-scrollbar-track]:my-4
          [&::-webkit-scrollbar-track]:rounded-full
            [&::-webkit-scrollbar-track]:bg-[#D8D8D6]
            [&::-webkit-scrollbar]:w-[3px]"
          >
            <SearchNearbyLocationListItem
              onSelectItem={() => {
                onSelectNearby()
                fetchUserLocation()
              }}
            />
            {SUGGESTED_PLACE_IDS.map((prediction) => (
              <LocationListItem
                key={prediction.placeId}
                description={prediction.description}
                placeId={prediction.placeId}
                onSelectItem={(placeId, description) => onSelectItem(placeId, description, true)}
              />
            ))}
          </ul>
        </div>
      ) : placePredictions.length > 0 ? (
        <div id="location-predictions" role="listbox" className="w-full">
          <ul className="flex max-h-[300px] flex-col gap-2 overflow-y-auto">
            {placePredictions.map((prediction) => (
              <LocationListItem
                key={prediction.place_id}
                description={prediction.description}
                placeId={prediction.place_id}
                onSelectItem={(placeId, description) => onSelectItem(placeId, description, false)}
              />
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-[13px] font-semibold">No results found</p>
      )}
    </div>
  )
}

export default LocationList
